import { FC, useEffect, useState } from "react";
import { Box, Button, Fab, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageZoom from "react-image-zooom";
import { useTranslation } from "react-i18next";

import { IconLeftArrowSvg, IconRightArrowSvg } from "../assets/svg";
import { useSelector } from "react-redux";
import { selectActivePage } from "../redux";

const keyPrefix = "layouts.ModalViewerZoomLayout";

/**
 * react-image-zooom llama a `onError` sin comprobar que exista. El aviso al
 * vendedor lo pinta `errorContent`, así que aquí no hay nada más que hacer.
 * Va fuera del componente a propósito: la librería recarga la imagen cada vez
 * que cambia esta función, y una nueva en cada render la recargaría siempre.
 */
const ignoreLoadError = (): void => {};

type ModalViewerZoomLayoutProps = {
  show: boolean;
  data: string[];
  onClose: () => void;
};

export const ModalViewerZoomLayout: FC<ModalViewerZoomLayoutProps> = ({
  show,
  data,
  onClose,
}): JSX.Element => {
  const { t } = useTranslation("translation", { keyPrefix });
  const [aspectRatio, setAspectRatio] = useState(1);
  const { currentIndexImage } = useSelector(selectActivePage);
  const [idx, setIdx] = useState<number>(0);
  const [retries, setRetries] = useState(0);

  // La página guardada puede no existir en esta revista: se muestra la última
  // que sí tiene. Sin páginas queda en -1 y no hay nada que mostrar. Se ajusta
  // aquí para el render en curso; el efecto de abajo lo fija en el estado.
  const pageIdx = Math.min(idx, data.length - 1);
  // Sin página no se pide ninguna imagen: un `src` undefined hace que el
  // navegador pida `/undefined` (VWA-27).
  const currentPage: string | undefined = data[pageIdx];

  const canBack = pageIdx > 0;
  const canForward = pageIdx < data.length - 1;
  const showArrows = data.length > 1;

  const back = (): void => {
    if (canBack) setIdx(pageIdx - 1);
  };

  const next = (): void => {
    if (canForward) setIdx(pageIdx + 1);
  };

  useEffect(() => {
    currentIndexImage !== undefined && setIdx(currentIndexImage);
  }, [currentIndexImage]);

  // Fija el ajuste en el estado: si la revista cambia después, el visor se
  // queda en la página que el vendedor está viendo y no salta a otra. Solo con
  // esto el primer render pediría una página inexistente; por eso también el
  // ajuste de `pageIdx`.
  useEffect(() => {
    if (data.length > 0 && idx > data.length - 1) setIdx(data.length - 1);
  }, [data.length, idx]);

  useEffect(() => {
    if (!show) return;
    // Sin dirección no hay nada que medir: marco neutro para el aviso.
    if (!currentPage) {
      setAspectRatio(1);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      setAspectRatio(ratio);
    };
    // Sin medida, un marco neutro: el de la página anterior podría dejar el
    // aviso de error angosto y recortado.
    img.onerror = () => setAspectRatio(1);
    img.src = currentPage;

    // Si el vendedor cambia de página antes de que esta cargue, su medición
    // ya no corresponde y no debe pisar la de la página nueva.
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [show, currentPage, retries]);

  const close = (): void => {
    setIdx(currentIndexImage !== undefined ? currentIndexImage : 0);
    onClose();
  };

  const notice = (message: string, canRetry = false): JSX.Element => (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Typography color="white" textAlign="center">
        {message}
      </Typography>
      {canRetry && (
        <Button variant="contained" onClick={() => setRetries((n) => n + 1)}>
          {t("retry")}
        </Button>
      )}
    </Box>
  );

  return (
    <Modal
      open={show}
      onClose={close}
      sx={{ backgroundColor: "#000000B3" }}
      disableAutoFocus
    >
      <Box
        sx={{
          position: "relative",
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: 4,
          paddingRight: 4,
          gap: 4
        }}
      >
        <IconButton
          aria-label={t("close")}
          sx={{ position: "absolute", right: 10, top: 10, color: "white" }}
          onClick={close}
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
        {showArrows && (
          <Fab
            aria-label={t("previous")}
            sx={{
              width: "50px",
              height: "50px",
              opacity: canBack ? 1 : 0.3,
              svg: {
                width: "44px",
                height: "44px",
              },
            }}
            disabled={!canBack}
            onClick={back}
          >
            <IconLeftArrowSvg />
          </Fab>
        )}
        <Box
          role="group"
          aria-label={t("pageFrame")}
          sx={{
            width: aspectRatio >= 1 ? "100%" : `${aspectRatio * 100}vh`,
            height: aspectRatio < 1 ? "100%" : `${100 / aspectRatio}vw`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {currentPage ? (
            // react-image-zooom no limpia su estado de error al cambiar de
            // `src`: con `key` cada página (y cada reintento) empieza de cero.
            <ImageZoom
              key={`${currentPage}#${retries}`}
              src={currentPage}
              alt={t("pageAlt", { page: pageIdx + 1, total: data.length })}
              zoom={300}
              width="100%"
              height="100%"
              onError={ignoreLoadError}
              errorContent={notice(t("pageError"), true)}
            />
          ) : (
            // Sin dirección no hay nada que pedir ni reintentar, pero el
            // vendedor tiene que ver qué pasa y qué puede hacer.
            notice(t(data.length > 1 ? "pageMissing" : "noPages"))
          )}
        </Box>

        {showArrows && (
          <Fab
            aria-label={t("next")}
            sx={{
              width: "50px",
              height: "50px",
              opacity: canForward ? 1 : 0.3,
              svg: {
                width: "44px",
                height: "44px",
              },
            }}
            disabled={!canForward}
            onClick={next}
          >
            <IconRightArrowSvg />
          </Fab>
        )}
      </Box>
    </Modal>
  );
};
